import React, { useEffect, useState } from 'react';
import { CDropdown, CDropdownItem, CDropdownMenu, CDropdownToggle, CFormLabel } from '@coreui/react';
import { Icon } from '@iconify/react'
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { dashboardNav } from '../components/NavigationPath';
import { calculatePercentage, formatDateToBackend } from '../components/Helper';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import LineChart from '../components/LineChart';
import PageHeader from '../components/PageHeader';
import DoughnutChart from '../components/DoughnutChart';
import DateSelection from '../components/DateSelection';

function ReportDashboard() {
    const { reportDashboard } = useReport();
    const { currency } = useCompanySetting();
    const { restaurantId } = useAuth();

    const targetedReceipts = 100;
    const targetedCustomers = 100;
    const currentDate = new Date();

    const [date, setDate] = useState(currentDate);
    const [filter, setFilter] = useState('revenues')
    const [isLoading, setIsLoading] = useState(false);
    const [targetedRevenue, setTargetedRevenue] = useState(500);
    const [revenueDetail, setRevenueDetail] = useState({
        totalRevenue: 0,
        targetedRevenue: targetedRevenue,
        percentageTargetAchieved: 0
    });
    const [revenueChart, setRevenueChart] = useState({
        labels: ['Revenue', 'Revenue'],
        data: [0, targetedRevenue]
    });
    const [receiptDetail, setReceiptDetail] = useState({
        totalReceipt: 0,
        targetedReceipt: targetedReceipts,
        percentageTargetAchieved: 0
    });
    const [receiptChart, setReceiptChart] = useState({
        labels: ['Receipts', 'Receipts'],
        data: [0, targetedReceipts]
    });
    const [customerDetail, setCustomerDetail] = useState({
        totalCustomer: 0,
        targetedCustomer: targetedCustomers,
        percentageTargetAchieved: 0
    });
    const [customerChart, setCustomerChart] = useState({
        labels: ['Customers', 'Customers'],
        data: [0, targetedCustomers]
    });
    const [dailyChartData, setDailyChartData] = useState({
        x: [],
        y: [],
        yLabel: ''
    });
    const [totalProductCost, setTotalProductCost] = useState(0)

    const startDate = date ? formatDateToBackend(date) : null;
    const endDate = date ? formatDateToBackend(date) : null;

    const fetchReceiptReportCharts = async () => {
        try {
            setIsLoading(true);
            const reports = await reportDashboard(startDate, endDate);
            const salesTarget = parseFloat(reports.salesTarget);
            setTargetedRevenue(salesTarget);

            if (reports.receiptReportData) {
                const totalRevenue = parseFloat(reports.receiptReportData.totalRevenue);
                const totalReceipt = parseFloat(reports.receiptReportData.totals.tickets);
                const totalCustomer = parseFloat(reports.receiptReportData.totals.totalCustomers);
                const percentageRevenueAchieved = calculatePercentage(totalRevenue, salesTarget);
                const percentageReceiptsAchieved = calculatePercentage(totalReceipt, targetedReceipts);
                const percentageCustomerAchieved = calculatePercentage(totalCustomer, targetedCustomers);

                setRevenueDetail({
                    totalRevenue: totalRevenue.toFixed(2),
                    targetedRevenue: salesTarget,
                    percentageTargetAchieved: percentageRevenueAchieved.toFixed(2)
                });

                setRevenueChart({
                    labels: ['Revenue', 'Revenue'],
                    data: [totalRevenue, salesTarget - totalRevenue]

                });

                setReceiptDetail({
                    totalReceipt: totalReceipt.toFixed(2),
                    targetedReceipt: targetedReceipts.toFixed(2),
                    percentageTargetAchieved: percentageReceiptsAchieved.toFixed(2)
                });

                setReceiptChart({
                    labels: ['Receipts', 'Receipts'],
                    data: [totalReceipt, targetedReceipts - totalReceipt]

                });

                setCustomerDetail({
                    totalCustomer: totalCustomer.toFixed(2),
                    targetedCustomer: targetedCustomers.toFixed(2),
                    percentageTargetAchieved: percentageCustomerAchieved.toFixed(2)
                })

                setCustomerChart({
                    labels: ['Customers', 'Customers'],
                    data: [totalCustomer, targetedCustomers - totalCustomer]

                });

                setTotalProductCost(reports.totalProductCost)
            }

            if (filter === 'revenues') {
                if (reports.hourlyRevenue.length > 0) {
                    const hours = reports.hourlyRevenue.map(item => item.hour);
                    const revenues = reports.hourlyRevenue.map(item => parseFloat(item.revenue));

                    setDailyChartData({
                        x: hours,
                        y: revenues,
                        yLabel: 'revenue'
                    });
                } else {
                    setDailyChartData({
                        x: [],
                        y: [],
                        yLabel: 'revenue'
                    });
                }
            } else if (filter === 'receipts') {
                if (reports.hourlyOrders.length > 0) {
                    const hours = reports.hourlyOrders.map(item => item.hour);
                    const orders = reports.hourlyOrders.map(item => parseFloat(item.orders));
                    setDailyChartData({
                        x: hours,
                        y: orders,
                        yLabel: 'receipts'
                    });
                } else {
                    setDailyChartData({
                        x: [],
                        y: [],
                        yLabel: 'receipts'
                    });
                }
            } else if (filter === 'customers') {
                if (reports.hourlyCustomers.length > 0) {
                    const hours = reports.hourlyCustomers.map(item => item.hour);
                    const customers = reports.hourlyCustomers.map(item => parseFloat(item.customers));
                    setDailyChartData({
                        x: hours,
                        y: customers,
                        yLabel: 'customers'
                    });
                } else {
                    setDailyChartData({
                        x: [],
                        y: [],
                        yLabel: 'customers'
                    });
                }
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching receipt report charts', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchReceiptReportCharts();
        }
    }, [restaurantId, startDate, endDate, filter]);

    return (
        <Layout2 items={dashboardNav.items} navTitle={dashboardNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Dashboard'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-end'>
                    <DateSelection
                        value={date}
                        onChange={setDate}
                    />
                </div>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div>
                        <div className='px-4 py-2 mt-5'>
                            <div className='w-100 text-start report-dashboard-chart p-3'>
                                <CFormLabel className='mb-3'>Daily
                                    <CDropdown className='ms-1'>
                                        <CDropdownToggle className='drop-down-button' value={filter}>{filter}</CDropdownToggle>
                                        <CDropdownMenu>
                                            <CDropdownItem onClick={() => setFilter('revenues')}>Revenues</CDropdownItem>
                                            <CDropdownItem onClick={() => setFilter('receipts')}>Receipts</CDropdownItem>
                                            <CDropdownItem onClick={() => setFilter('customers')}>Customers</CDropdownItem>
                                        </CDropdownMenu>
                                    </CDropdown>
                                </CFormLabel>
                                {
                                    dailyChartData.x.length > 0 && dailyChartData.y.length > 0 && (
                                        <LineChart
                                            x={dailyChartData.x}
                                            y={dailyChartData.y}
                                            yLabel={dailyChartData.yLabel}
                                            backgroundColor={true}
                                        />
                                    )
                                }
                            </div>
                        </div>
                        <div className="ms-4 my-5">
                            <div className="row w-100">
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                                    <div className="d-flex justify-content-between p-3 report-dashboard-chart">
                                        <div className="d-flex flex-column justify-content-center m-0">
                                            <p className="mb-0">
                                                <span className="fw-bold fs-4">
                                                    {currency}{revenueDetail.totalRevenue}
                                                </span>
                                                <small className="fw-bold text-body-secondary">
                                                    /{currency}{targetedRevenue.toFixed(2)}
                                                </small>
                                            </p>
                                            <p className="m-0 fw-bold text-body-secondary">
                                                <small>REVENUE TARGET</small>
                                            </p>
                                        </div>
                                        <div>
                                            {revenueChart.labels.length > 0 &&
                                                revenueChart.data.length > 0 && (
                                                    <DoughnutChart
                                                        labels={revenueChart.labels}
                                                        data={revenueChart.data}
                                                        centerText={`${revenueDetail.percentageTargetAchieved}%`}
                                                    />
                                                )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                                    <div className="d-flex justify-content-between p-3 report-dashboard-chart">
                                        <div className="d-flex flex-column justify-content-center m-0">
                                            <p className="mb-0">
                                                <span className="fw-bold fs-4">
                                                    {receiptDetail.totalReceipt}
                                                </span>
                                                <small className="fw-bold text-body-secondary">
                                                    /{receiptDetail.targetedReceipt}
                                                </small>
                                            </p>
                                            <p className="m-0 fw-bold text-body-secondary">
                                                <small>RECEIPTS</small>
                                            </p>
                                        </div>
                                        <div>
                                            {receiptChart.labels.length > 0 &&
                                                receiptChart.data.length > 0 && (
                                                    <DoughnutChart
                                                        labels={receiptChart.labels}
                                                        data={receiptChart.data}
                                                        centerText={`${receiptDetail.percentageTargetAchieved}%`}
                                                    />
                                                )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                                    <div className="d-flex justify-content-between p-3 report-dashboard-chart">
                                        <div className="d-flex flex-column justify-content-center m-0">
                                            <p className="mb-0">
                                                <span className="fw-bold fs-4">
                                                    {customerDetail.totalCustomer}
                                                </span>
                                                <small className="fw-bold text-body-secondary">
                                                    /{customerDetail.targetedCustomer}
                                                </small>
                                            </p>
                                            <p className="m-0 fw-bold text-body-secondary">
                                                <small>CUSTOMER TARGET</small>
                                            </p>
                                        </div>
                                        <div>
                                            {customerChart.labels.length > 0 &&
                                                customerChart.data.length > 0 && (
                                                    <DoughnutChart
                                                        labels={customerChart.labels}
                                                        data={customerChart.data}
                                                        centerText={`${customerDetail.percentageTargetAchieved}%`}
                                                    />
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row w-100">
                                <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                                    <div className="d-flex justify-content-between p-3 report-dashboard-chart">
                                        <div className="d-flex flex-column justify-content-center m-0">
                                            <p className="mb-0">
                                                <span className="fw-bold fs-4">
                                                    {currency}{totalProductCost}
                                                </span>
                                            </p>
                                            <p className="m-0 fw-bold text-body-secondary">
                                                <small>SALES COST</small>
                                            </p>
                                        </div>
                                        <div className='mt-1'>
                                            <Icon icon="material-symbols:account-balance-wallet-outline" width="50" height="50" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout2>
    );
}

export default ReportDashboard;
